import React, { useEffect, useState, useRef } from 'react';
import { Platform } from 'react-native';
import { BottomTabBar } from '@cubemeet/ui';
import { bottomItems, getRouteForKey } from '../config/bottomNav';
import useHideOnScroll from '../hooks/useHideOnScroll';

interface BottomNavPortalProps {
  activeIndex?: number;
  disableAutoPadding?: boolean;
}

const BottomNavPortal: React.FC<BottomNavPortalProps> = ({ activeIndex = 2, disableAutoPadding = false }) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const { hidden } = useHideOnScroll({ autoDetect: true, listenOnWindow: true });

  // Footer visual constants — adjust if BottomTabBar visual height changes
  // Match AppLayout's content bottom spacing (120) so pages look identical
  const FOOTER_VISUAL_HEIGHT_PX = 120; // includes safe area fallback + visual padding

  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    try {
      const g: any = (globalThis as any) || {};
      if (g && g.document && g.document.body) {
        let el = g.document.getElementById('app-bottom-nav-portal');
        if (!el) {
          el = g.document.createElement('div');
          el.id = 'app-bottom-nav-portal';
          el.setAttribute('data-bottom-nav-portal', '1');
          g.document.body.appendChild(el);
        } else {
          if (el.parentNode !== g.document.body) g.document.body.appendChild(el);
          try { el.setAttribute('data-bottom-nav-portal', '1'); } catch (e) {}
        }
        setContainer(el);
      }
    } catch (e) {
      // ignore
    }

    // Ensure the page content reserves space for the fixed footer so it doesn't overlap.
    // Skip auto-padding if disabled
    if (disableAutoPadding) return undefined;
    
    try {
      const pad = `calc(env(safe-area-inset-bottom, 16px) + ${FOOTER_VISUAL_HEIGHT_PX}px)`;
      // Prefer adjusting the React root container if present so we don't accidentally affect other scripts/styles
      const appRoot = document.getElementById('root');
      if (appRoot && appRoot.style) {
        const prev = appRoot.style.paddingBottom || '';
        appRoot.style.paddingBottom = pad;
        return () => { try { appRoot.style.paddingBottom = prev; } catch (e) {} };
      }

      const prevBody = (document.body && document.body.style && document.body.style.paddingBottom) || '';
      if (document && document.body) document.body.style.paddingBottom = pad;
      return () => { try { if (document && document.body) document.body.style.paddingBottom = prevBody; } catch (e) {} };
    } catch (e) {
      // ignore
    }

    return undefined;
  }, [disableAutoPadding]);

  

  // use centralized bottom items for web
  const webBottomItems = bottomItems.map((it) => ({ ...it }));

  const handleTabPress = (index: number, key: string) => {
    try {
      const target = getRouteForKey(key);
      if (typeof window !== 'undefined') {
        window.history.pushState({}, '', target);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    } catch (e) {}
  };

  const footerNode = (
    <div className="app-bottom-nav-root" style={{
      position: 'fixed',
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 2147483647,
      width: '100%',
      boxSizing: 'border-box',
      pointerEvents: hidden ? 'none' : 'auto',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-end',
      transform: hidden ? 'translateY(110%)' : 'translateY(0)',
      transition: 'transform 240ms ease'
    }}>
      <div style={{
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        justifyContent: 'center'
      }}>
        <div style={{
          width: '100%',
          maxWidth: 600,
          margin: '0 auto',
          boxSizing: 'border-box',
          backgroundColor: 'rgba(255,255,255,1)',
          borderTop: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 -6px 20px rgba(0,0,0,0.06)',
          paddingTop: 10,
          paddingBottom: 'calc(env(safe-area-inset-bottom, 16px) + 14px)',
          paddingLeft: 12,
          paddingRight: 12,
        }}>
          <BottomTabBar items={webBottomItems} activeIndex={activeIndex} onPress={handleTabPress} showRaisedCenter onCenterPress={() => { if (typeof window !== 'undefined') { window.history.pushState({}, '', '/travel/create'); window.dispatchEvent(new PopStateEvent('popstate')); } }} />
        </div>
      </div>
    </div>
  );

  if (Platform.OS !== 'web') return null;

  try {
    // prefer synchronous require
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { createPortal } = require('react-dom');
    if (container) return createPortal(footerNode, container);
  } catch (e) {
    // fallback
  }

  return footerNode;
};

export default BottomNavPortal;
